
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ErrorService } from '@app/common/services/error.service';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { State, toODataString } from '@progress/kendo-data-query';
import { Table } from 'app/shared/models';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Filterable } from './data-api-interfaces';
import { LookupMapper } from './lookup-mapper';

export class GridApiContext extends BehaviorSubject<GridDataResult> {
  protected tableName: string;
  protected requestOptions: HttpParams = null;
  protected table: Table;

  constructor(private http: HttpClient, protected BASE_URL: string,
    table: Table | string, public filtermaker: Filterable,
    public expandTerm: string,
    protected errorService: ErrorService,
    private tableIDField: string = null
  ) {
    super(null);
    if (typeof table === 'string') {
      this.tableName = table;
      if (!tableIDField) {
        throw new Error('When passing a string for the table into the constructor of GridApiContext' +
          ', you must provide a tableIDField which is the unique identifier of the returned result');
      }
    } else {
      this.tableName = Table[table];
      this.table = table as Table;
      if (!this.tableIDField) {
        this.tableIDField = LookupMapper.getIdColumn(table);
      }
    }
  }

  public static removeObjects(data: any): void {
    for (let key in data) {
      if (data.hasOwnProperty(key)) {
        let m = data[key];
        if (typeof m === 'object' && !(m instanceof Date)) {
          delete data[key];
        }
      }
    }
  }

  private create(data: any) {
    data[this.tableIDField] = 0;
    let url = `${this.BASE_URL}${this.tableName}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .post(url, data, { headers: headers }).pipe(
        catchError((e) => this.errorService.handleHTTPError(e))
      );
  }

  private update(data: any, full: boolean = false): Observable<Object> {
    let url = `${this.BASE_URL}${this.tableName}`;
    url += `(${data[this.tableIDField]})`;
    if (full) {
      return this.http.put(url, data).pipe(
        catchError(
          (e) => {
            return this.errorService.handleHTTPError(e);
          })
      );
    } else {
      return this.http.patch(url, data).pipe(
        catchError((e) => this.errorService.handleHTTPError(e)));
    }
  }

  protected post(endpoint: string, data: any): Observable<Object> {
    let url = `${this.BASE_URL}${endpoint}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    let options = new HttpParams({});
    let response = this.http
      .post(url, data, { headers: headers }).pipe(
        catchError((err) => this.errorService.handleHTTPError(err)
        ));
    return response;
  }

  protected filterToString({ filter }: { filter?: string }): string {
    return (filter && typeof filter === 'string') ? `&$filter=${filter}` : '';
  }

  protected buildGetUrl(tableName: string, state?: any): string {
    let queryStr = `${toODataString(state)}&$count=true${this.filterToString(state)}`;

    if (this.expandTerm) {
      queryStr += '&$expand=' + this.expandTerm;
    }
    let url = `${this.BASE_URL}${tableName}?${queryStr}`;
    return url;
  }

  protected fetchMap(jsonResult: any): GridDataResult {
    return {
      data: jsonResult.value,
      total: parseInt(jsonResult.odata ? jsonResult.odata.count : '', 10)
    };
  }

  protected fetch(tableName: string, state: any): Observable<GridDataResult> {
    let url = this.buildGetUrl(tableName, state);
    let options = {
      headers: new HttpHeaders(),
      params: new HttpParams()
    };
    let x = this.http
      .get(url, options).pipe(
        map(response => response),
        map(response => this.fetchMap(response)),);
    return x;
  }

  public query(state?: State): BehaviorSubject<any> {
    let name = this.tableName || Table[this.table];
    if (!state) {
      state = {
        skip: 0,
        take: 50
      }
    };
    let fetchState: State = state;

    if (this.filtermaker) {
      fetchState = this.filtermaker.getGridFilter(state);
    }

    if (!fetchState.take) {
      fetchState.take = 50;
    }

    this.fetch(name, fetchState)
      .subscribe(x => super.next(x));

    return this;
  }

  public save(data: any, isNew?: boolean, removeObjects: boolean = false, usePut: boolean = false): Observable<any> {
    let copy = data;
    if (removeObjects) {
      copy = Object.assign({}, data);
      GridApiContext.removeObjects(copy);
    }
    if (isNew) {
      let result = this.create(copy);
      result.pipe(map<any, any>((r, index: number) => {
        if (r.ok) {
          let m = r.then(v => {
            data[this.tableIDField] = v[this.tableIDField];
          });
        }
        return r;
      }));
      return result;
    } else {
      return this.update(copy, usePut);
    }
  }

  public remove(data: any) {
    let url = `${this.BASE_URL}${this.tableName}(${data[this.tableIDField]})`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    let options = {
      headers,
      params: new HttpParams()
    };
    return this.http
      .delete(url, options).toPromise();
  }
}